import { useState, type CSSProperties } from 'react';
import { COUNCIL_REVIEWER_SLUGS, hasRole } from '@death-star/shared';
import { useApiMutation } from '../use-api.js';
import { useRole } from '../use-role.js';
import type { Annotation } from '../lib/annotations.js';
import { isSafeUrl } from '../routes/suppression-form-helpers.js';

const inputStyle: CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
};

const labelStyle: CSSProperties = { display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' };

const cell: CSSProperties = { padding: '8px 16px', verticalAlign: 'top' };

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

// The chart markers carry only a short label; this is where an annotation's
// detail, link and author can actually be read — and, for triagers, where a
// new one is added. The server's triager gate on POST /api/annotations is the
// control; hiding the form from viewers is cosmetic, same as the suppressions
// create form.
export function AnnotationsPanel({ annotations }: { annotations: Annotation[] }) {
  const { role, loading: roleLoading } = useRole();
  const canWrite = !roleLoading && hasRole(role, 'triager');
  const mutation = useApiMutation('/api/annotations');
  const [date, setDate] = useState(todayStr());
  const [reviewer, setReviewer] = useState('*');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [url, setUrl] = useState('');
  const errMessage = mutation.error instanceof Error ? mutation.error.message : null;
  const canSubmit = title.trim().length > 0 && !mutation.isPending;

  // Only reachable through the button, which is disabled until canSubmit.
  // The fields clear only once the write lands: a rejected submit (the per-day
  // cap, a bad url) keeps what was typed so it can be fixed and resent.
  function submit() {
    mutation.mutate({
      effective_date: date,
      reviewer,
      title: title.trim(),
      ...(detail.trim() ? { detail: detail.trim() } : {}),
      ...(url.trim() ? { url: url.trim() } : {}),
    }, {
      onSuccess: () => {
        setTitle('');
        setDetail('');
        setUrl('');
      },
    });
  }

  return (
    <div style={{ marginTop: 24 }}>
      <h3 id="annotations-heading" style={{ fontSize: '0.875rem', fontWeight: 664, marginBottom: 8, color: 'var(--loop-neutral-900)' }}>
        Annotations
      </h3>
      <p style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-700)', marginTop: 0, marginBottom: 8 }}>
        The dashed lines on the charts. Model changes are detected automatically from the
        models each council run reports; anything else significant (a prompt rewrite, a policy
        change) can be tagged here. Annotations explain a chart — they never silence a drift alert.
      </p>
      {annotations.length === 0 ? (
        <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.75rem' }}>No annotations in this window.</p>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table aria-labelledby="annotations-heading" style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
            <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
              <tr>
                {['Date', 'Reviewer', 'Change', 'By'].map(h => (
                  <th key={h} style={{ ...cell, color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...annotations].reverse().map(a => (
                <tr key={a.id + a.effective_date} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>{a.effective_date}</td>
                  <td style={{ ...cell, fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.reviewer === '*' ? 'all' : a.reviewer}</td>
                  <td style={cell}>
                    {a.url && isSafeUrl(a.url)
                      ? <a href={a.url} target="_blank" rel="noopener noreferrer">{a.title}</a>
                      : a.title}
                    {a.detail && <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-700)' }}>{a.detail}</div>}
                  </td>
                  <td style={{ ...cell, fontSize: '0.75rem', color: 'var(--loop-neutral-700)' }}>
                    {a.kind === 'model_change' ? 'auto-detected' : a.author}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canWrite && (
        <div className="card" style={{ marginTop: 12, padding: 'var(--loop-space-300)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 220px 1fr', gap: 12 }}>
            <div>
              <label htmlFor="annotation-date" style={labelStyle}>Date (UTC)</label>
              <input id="annotation-date" type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label htmlFor="annotation-reviewer" style={labelStyle}>Reviewer</label>
              <select id="annotation-reviewer" value={reviewer} onChange={e => setReviewer(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                <option value="*">All reviewers</option>
                {COUNCIL_REVIEWER_SLUGS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="annotation-title" style={labelStyle}>What changed</label>
              <input
                id="annotation-title"
                type="text"
                value={title}
                maxLength={120}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. correctness prompt rewrite"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ gridColumn: '1 / 3' }}>
              <label htmlFor="annotation-url" style={labelStyle}>Link (optional, https)</label>
              <input
                id="annotation-url"
                type="text"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://github.com/…/pull/123"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label htmlFor="annotation-detail" style={labelStyle}>Detail (optional)</label>
              <input
                id="annotation-detail"
                type="text"
                value={detail}
                maxLength={1000}
                onChange={e => setDetail(e.target.value)}
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <button
            className="btn-sm"
            disabled={!canSubmit}
            onClick={submit}
            style={{ marginTop: 12, background: canSubmit ? 'var(--loop-brand-600)' : 'var(--loop-neutral-300)', color: 'var(--loop-neutral-0)', border: 'none', borderRadius: 'var(--loop-radius-sm)', cursor: canSubmit ? 'pointer' : 'not-allowed' }}
          >
            {mutation.isPending ? 'Adding…' : 'Add annotation'}
          </button>
          {errMessage && <p className="error">{errMessage}</p>}
        </div>
      )}
    </div>
  );
}
